// Registry các Avatar workspace đã từng được dùng trên máy này — `~/.avatar/workspaces.json`.
//
// Vì sao cần (#211): `avatar sync` bám 1 cwd, nên nhiều workspace ⇒ phải `cd` từng
// nơi. Registry là nguồn để `avatar sync --all` (phase-02) biết máy này có những
// workspace nào mà KHÔNG phải quét đĩa (đo 2026-08-20: 42 marker trên máy dev, phần
// lớn là thư mục thử).
//
// 3 ràng buộc thiết kế, đều có lý do đo được:
//
//  1. `registryPath` là THAM SỐ, không đọc `AVATAR_HOME` trong thân hàm. `AVATAR_HOME`
//     tính LÚC IMPORT (`user-config-store.ts`) ⇒ unit test đổi `process.env.HOME` sau
//     import là vô tác dụng và sẽ ghi vào `~/.avatar` THẬT của máy dev. Test tiêm
//     thư mục tạm qua tham số này (cùng khuôn `detectOpenClaudeCodeSession(root, homeDir, now)`).
//  2. Ghi **mode 0600**. `~/.avatar` KHÔNG phải thư mục kín — `ensureAvatarHome()` gọi
//     `ensureDir()` không truyền mode ⇒ 0755 theo umask; thứ duy nhất được bảo vệ ở đó
//     là `config.json`, bằng file mode riêng. Registry chứa path (username + cấu trúc
//     thư mục làm việc) nên tự bảo vệ bằng mode của chính nó.
//  3. Fail-soft khi đọc: file thiếu/hỏng/sai schema ⇒ coi như rỗng. Registry là tiện
//     ích, KHÔNG được làm hỏng lệnh chính (`init`/`sync`/`status`/`doctor`).
import { realpath } from "node:fs/promises";
import { join, resolve } from "node:path";
import { z } from "zod";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import { isNonDevWorkspace } from "./read-workspace-marker-kind.js";
import { resolveAvatarWorkspaceRootFromCwd } from "./resolve-avatar-workspace-root-from-cwd.js";
import { AVATAR_HOME } from "./user-config-store.js";
import { WORKSPACE_MARKER_RELATIVE } from "./workspace-marker-constants.js";

export const DEFAULT_REGISTRY_PATH = join(AVATAR_HOME, "workspaces.json");

// Owner-only read/write. Xem ghi chú (2) ở đầu file.
const REGISTRY_FILE_MODE = 0o600;

export const workspaceEntrySchema = z.object({
  /** Workspace root TUYỆT ĐỐI (đã `resolve`) — nơi có `.claude/avatar.json`. */
  path: z.string().min(1),
  registeredAt: z.iso.datetime(),
  /** Lần cuối một lệnh avatar chạm workspace này — đầu vào luật `stale` của `sync --all`. */
  lastSeenAt: z.iso.datetime(),
  /**
   * Lần cuối workspace này được sync THÀNH CÔNG. Phase-02 ghi sau khi tiến trình con
   * exit 0. Optional vì entry vừa đăng ký thì chưa có lần sync nào.
   */
  lastSyncAt: z.iso.datetime().optional(),
  /**
   * Workspace do AUTOPILOT tạo và quản lý (mission workspace), không phải workspace
   * người dùng tự `avatar init`. P9 dùng cờ này để `avatar sync --all` BỎ QUA nó:
   * pack trong mission workspace phải bất biến suốt mission (§9.4), sync giữa chừng
   * sẽ tráo hook/protocol ngay dưới chân một attempt đang chạy.
   *
   * ADDITIVE + OPTIONAL (§21): CLI cũ đọc file có field này vẫn parse bình thường
   * (zod bỏ qua key lạ), và `recordWorkspaceInRegistry` cũ không hề ghi nó.
   */
  managedMission: z.boolean().optional(),
  /** Mission đang sở hữu workspace này — đi kèm `managedMission`. */
  missionId: z.string().optional(),
});

export const workspaceRegistrySchema = z.object({
  workspaces: z.array(workspaceEntrySchema).default([]),
});

export type WorkspaceEntry = z.infer<typeof workspaceEntrySchema>;

/**
 * Chuẩn hoá path về dạng CANONICAL (đã resolve symlink) — nền của tính idempotent.
 *
 * ⚠️ Không có bước này thì cùng một workspace mọc 2 entry: `init` truyền
 * `args.workspacePath` do người dùng gõ (chưa resolve symlink), còn `status`/`doctor`
 * đi qua `process.cwd()` mà Node LUÔN trả realpath. Đo được ngay trên macOS: `/var/...`
 * và `/private/var/...` là cùng một thư mục nhưng khác chuỗi (`/var` là symlink).
 * Hệ quả nếu bỏ qua: `sync --all` chạy 2 lần cho 1 workspace, và luật `same-version`
 * của phase-02 đọc 2 lần cùng một `.claude/pack`.
 *
 * Path chưa tồn tại (hiếm — caller vừa xoá thư mục) ⇒ `realpath` ném ⇒ lùi về `resolve`.
 */
async function canonicalize(path: string): Promise<string> {
  try {
    return await realpath(path);
  } catch {
    return resolve(path);
  }
}

/** Entry sắp theo `path` ⇒ file ổn định giữa các lần ghi, dễ so bằng mắt. */
function sortByPath(entries: WorkspaceEntry[]): WorkspaceEntry[] {
  return [...entries].sort((a, b) => a.path.localeCompare(b.path));
}

export async function writeEntries(registryPath: string, entries: WorkspaceEntry[]): Promise<void> {
  await writeJsonAtomic(registryPath, { workspaces: sortByPath(entries) }, REGISTRY_FILE_MODE);
}

/**
 * Đọc registry. KHÔNG prune ở đây — dọn entry chết là việc của `pruneMissingWorkspaces`,
 * để lệnh chỉ-đọc không âm thầm ghi đĩa.
 *
 * Fail-soft: thiếu file / JSON hỏng / sai schema ⇒ `[]`. Không bao giờ throw.
 */
export async function readWorkspaceRegistry(
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<WorkspaceEntry[]> {
  if (!(await pathExists(registryPath))) return [];
  try {
    const parsed = workspaceRegistrySchema.safeParse(await readJson<unknown>(registryPath));
    return parsed.success ? sortByPath(parsed.data.workspaces) : [];
  } catch {
    return []; // JSON hỏng / không đọc được — xem ghi chú (3) đầu file.
  }
}

/**
 * Ghi nhận workspace vào registry. IDEMPOTENT: gọi N lần cho cùng path ⇒ vẫn 1 entry,
 * chỉ `lastSeenAt` nhích. `registeredAt` và `lastSyncAt` của entry cũ giữ nguyên.
 *
 * ⚠️ `workspaceRoot` phải là WORKSPACE ROOT, không phải `process.cwd()`. `status`/`doctor`
 * KHÔNG tự resolve root (chúng dùng thẳng cwd) ⇒ call site ở đó phải đi qua
 * `resolveAvatarWorkspaceRootFromCwd()` trước, nếu không sẽ ghi path subdir vào registry
 * và `sync --all` spawn tiến trình con sai cwd.
 *
 * Đọc-sửa-ghi ngay trước khi ghi + ghi atomic: 2 tiến trình `avatar` chạy song song thì
 * cùng lắm mất 1 lượt cập nhật `lastSeenAt` — chấp nhận được, đây không phải dữ liệu tiền.
 */
export async function recordWorkspaceInRegistry(
  workspaceRoot: string,
  registryPath: string = DEFAULT_REGISTRY_PATH,
  now: Date = new Date(),
): Promise<boolean> {
  // #254: workspace `kind` ≠ dev (vd Avatar DataHub) KHÔNG vào registry ⇒ `sync --all` không chạm.
  if (isNonDevWorkspace(workspaceRoot)) return false;
  const absolute = await canonicalize(workspaceRoot);
  const stamp = now.toISOString();
  const entries = await readWorkspaceRegistry(registryPath);
  const existing = entries.find((e) => e.path === absolute);

  const next = existing
    ? entries.map((e) => (e.path === absolute ? { ...e, lastSeenAt: stamp } : e))
    : [...entries, { path: absolute, registeredAt: stamp, lastSeenAt: stamp }];

  await writeEntries(registryPath, next);
  return true;
}

/**
 * Ghi nhận MISSION WORKSPACE (do autopilot tạo) vào registry — hàm ADDITIVE, B30.
 *
 * Vì sao phải là hàm mới chứ không sửa `recordWorkspaceInRegistry`: hàm đó dựng entry
 * CỐ ĐỊNH `{path, registeredAt, lastSeenAt}` và không nhận field bổ sung, còn
 * `markWorkspaceSynced` chỉ set `lastSyncAt` ⇒ không có đường nào ghi được field mới.
 * Sửa chữ ký hàm cũ sẽ đổi byte của entry đường interactive (§21 cấm) — nên tách hàm.
 *
 * IDEMPOTENT: gọi N lần cùng path ⇒ vẫn 1 entry; `registeredAt`/`lastSyncAt` của entry
 * cũ GIỮ NGUYÊN, chỉ `lastSeenAt` nhích và cờ managed được set.
 */
export async function recordManagedMissionWorkspace(
  workspaceRoot: string,
  missionId: string,
  registryPath: string = DEFAULT_REGISTRY_PATH,
  now: Date = new Date(),
): Promise<void> {
  const absolute = await canonicalize(workspaceRoot);
  const stamp = now.toISOString();
  const entries = await readWorkspaceRegistry(registryPath);
  const existing = entries.find((e) => e.path === absolute);

  const next = existing
    ? entries.map((e) =>
        e.path === absolute ? { ...e, lastSeenAt: stamp, managedMission: true, missionId } : e,
      )
    : [
        ...entries,
        {
          path: absolute,
          registeredAt: stamp,
          lastSeenAt: stamp,
          managedMission: true,
          missionId,
        },
      ];

  await writeEntries(registryPath, next);
}

/**
 * Đánh dấu workspace vừa sync thành công (phase-02 gọi sau khi tiến trình con exit 0).
 * Set `lastSyncAt` VÀ `lastSeenAt` — vừa sync xong thì đương nhiên cũng vừa "thấy", để
 * một lần `sync --all` không làm workspace trôi dần về phía ngưỡng `stale`.
 * Trả `false` nếu workspace không có trong registry — KHÔNG tự tạo entry: `sync --all`
 * chỉ chạy trên entry đã có, entry lạ xuất hiện ở đây nghĩa là caller nhầm path.
 */
export async function markWorkspaceSynced(
  workspaceRoot: string,
  at: Date,
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<boolean> {
  const absolute = await canonicalize(workspaceRoot);
  const entries = await readWorkspaceRegistry(registryPath);
  if (!entries.some((e) => e.path === absolute)) return false;

  const stamp = at.toISOString();
  await writeEntries(
    registryPath,
    entries.map((e) => (e.path === absolute ? { ...e, lastSyncAt: stamp, lastSeenAt: stamp } : e)),
  );
  return true;
}

/**
 * Đăng ký workspace suy từ `cwd` — dành cho lệnh KHÔNG tự resolve workspace root.
 *
 * ⚠️ `status` và `doctor` dùng thẳng `process.cwd()` (`status.ts` → `gatherStatus(cwd)` chỉ
 * kiểm `pathExists(cwd/.claude)`; `doctor.ts` → `runChecks(cwd)`); cả hai KHÔNG đọc marker
 * và KHÔNG đi ngược cây. Đăng ký thẳng `cwd` ở đó hỏng 2 đường: gõ ở **subdir** ⇒ registry
 * ghi path subdir ⇒ `sync --all` spawn tiến trình con sai cwd; gõ ở `~` ⇒ máy nào chạy
 * Claude Code cũng có `~/.claude` ⇒ ghi `~` vào registry. Nên đường vào phải qua
 * `resolveAvatarWorkspaceRootFromCwd` và BỎ QUA khi trả `null`.
 *
 * Trả workspace root đã đăng ký, hoặc `null` khi cwd không thuộc Avatar workspace nào
 * hoặc workspace thuộc loại non-dev (#254).
 */
export async function recordWorkspaceFromCwd(
  cwd: string,
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<string | null> {
  const workspaceRoot = resolveAvatarWorkspaceRootFromCwd(cwd);
  if (workspaceRoot === null) return null;
  // Canonical hoá MỘT lần ở đây rồi truyền xuống: `recordWorkspaceInRegistry` gọi
  // `canonicalize` lần nữa cũng ra chính giá trị này (hàm idempotent), nhưng caller cần
  // bản canonical để so với entry trong registry nên tính sẵn, khỏi realpath 2 lượt.
  const canonical = await canonicalize(workspaceRoot);
  return (await recordWorkspaceInRegistry(canonical, registryPath)) ? canonical : null;
}

/** Workspace còn sống = còn marker `.claude/avatar.json` tại path đã ghi. */
async function workspaceStillExists(entryPath: string): Promise<boolean> {
  return await pathExists(join(entryPath, WORKSPACE_MARKER_RELATIVE));
}

/**
 * Dọn entry trỏ tới workspace đã bị xoá/di chuyển (mất marker). Chỉ XOÁ entry chết,
 * KHÔNG BAO GIỜ tạo entry mới ⇒ gọi lặp lại là an toàn.
 *
 * Không có gì để dọn ⇒ KHÔNG ghi file (tránh đụng mtime vô cớ mỗi lần `sync --all`).
 */
export async function pruneMissingWorkspaces(
  registryPath: string = DEFAULT_REGISTRY_PATH,
): Promise<{ kept: WorkspaceEntry[]; pruned: string[] }> {
  const entries = await readWorkspaceRegistry(registryPath);
  const kept: WorkspaceEntry[] = [];
  const pruned: string[] = [];

  for (const entry of entries) {
    if (await workspaceStillExists(entry.path)) kept.push(entry);
    else pruned.push(entry.path);
  }

  if (pruned.length > 0) await writeEntries(registryPath, kept);
  return { kept, pruned };
}
